import { type FC, useEffect, useRef, useState } from "react";
import mermaid from "mermaid";
import { cn } from "@/components/podkit/lib/cn";
import { MarkdownCopyablePre } from "@/components/ai/chat/MarkdownCopyablePre";
import { Fullscreen, Maximize2, Minimize2, ZoomIn, ZoomOut, FileText, Code } from "lucide-react";
import { Tabs, TabsList, TabsTrigger } from "@/components/flexkit/tabs/Tabs";
import { Banner } from "@/components/flexkit/Banner";
import type { BaseIconProps } from "@/assets/icons/utils";

type MermaidCodePreviewProps = {
    content: string;
    className?: string;
};

// Icon wrappers for TabsTrigger compatibility
const CodeIcon: FC<BaseIconProps> = ({ size: _size = "sm", ...props }) => <Code size={16} {...props} />;
const FileTextIcon: FC<BaseIconProps> = ({ size: _size = "sm", ...props }) => <FileText size={16} {...props} />;

export const MermaidCodePreview: FC<MermaidCodePreviewProps> = ({ content, className = "" }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [isPreviewMode, setIsPreviewMode] = useState(false);
    const [isRendering, setIsRendering] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [svgContent, setSvgContent] = useState<string | null>(null);
    const [zoomLevel, setZoomLevel] = useState(1);
    const [isFullscreen, setIsFullscreen] = useState(false);

    // Initialize mermaid once
    useEffect(() => {
        mermaid.initialize({
            startOnLoad: false,
            theme: "base",
            themeVariables: {
                primaryColor: "#3b82f6",
                primaryTextColor: "#1f2937",
                primaryBorderColor: "#d1d5db",
                lineColor: "#6b7280",
                secondaryColor: "#f3f4f6",
                tertiaryColor: "#ffffff",
            },
            flowchart: {
                useMaxWidth: true,
                htmlLabels: true,
            },
            sequence: {
                useMaxWidth: true,
            },
            gantt: {
                useMaxWidth: true,
            },
        });
    }, []);

    const renderDiagram = async () => {
        if (!content || svgContent) return; // Already rendered

        try {
            setIsRendering(true);
            setError(null);

            // Generate a unique ID for this diagram
            const diagramId = `mermaid-${Math.random().toString(36).substr(2, 9)}`;

            // Render the diagram
            const { svg } = await mermaid.render(diagramId, content);
            setSvgContent(svg);
        } catch (err) {
            console.error("Mermaid rendering error:", err);
            setError(err instanceof Error ? err.message : "Failed to render diagram");
        } finally {
            setIsRendering(false);
        }
    };

    const handlePreviewToggle = () => {
        if (!isPreviewMode && !svgContent && !error) {
            void renderDiagram();
        }
        setIsPreviewMode(!isPreviewMode);
    };

    const handleZoomIn = () => {
        setZoomLevel((prev) => Math.min(prev + 0.25, 4.5));
    };

    const handleZoomOut = () => {
        setZoomLevel((prev) => Math.max(prev - 0.25, 0.25));
    };

    const handleFitToScreen = () => {
        setZoomLevel(1);
    };

    const handleFullscreen = () => {
        if (!containerRef.current) return;

        if (!isFullscreen) {
            if (containerRef.current.requestFullscreen) {
                void containerRef.current.requestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                void document.exitFullscreen();
            }
        }
        setIsFullscreen(!isFullscreen);
    };

    // Listen for fullscreen changes
    useEffect(() => {
        const handleFullscreenChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };

        document.addEventListener("fullscreenchange", handleFullscreenChange);
        return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    }, []);

    return (
        <div className={cn("my-4", className)} ref={containerRef}>
            <Tabs
                value={isPreviewMode ? "preview" : "code"}
                onValueChange={(value) => {
                    if (value === "preview") {
                        handlePreviewToggle();
                    } else {
                        setIsPreviewMode(false);
                    }
                }}
                className="w-full"
            >
                <TabsList className="w-fit">
                    <TabsTrigger value="code" LeadingIcon={CodeIcon}>
                        Code
                    </TabsTrigger>
                    <TabsTrigger value="preview" LeadingIcon={FileTextIcon}>
                        Preview
                        {isRendering && (
                            <span className="ml-1 inline-block h-3 w-3 animate-spin rounded-full border-2 border-current border-t-transparent" />
                        )}
                    </TabsTrigger>
                </TabsList>

                {/* Content area */}
                <div className="mt-3 rounded-md border border-border-subtle bg-surface-muted">
                    {!isPreviewMode ? (
                        // Code view - Fixed copy functionality
                        <MarkdownCopyablePre className="px-4 py-3 font-mono text-sm">
                            <code className="language-mermaid">{content}</code>
                        </MarkdownCopyablePre>
                    ) : (
                        // Preview view with zoom controls
                        <div className="p-4">
                            {error ? (
                                <Banner
                                    variant="danger"
                                    text={`Diagram Error: ${error}`}
                                    action={{
                                        text: "Show Code",
                                        onClick: () => setIsPreviewMode(false),
                                        "data-tracking-id": "show-code-mermaid-error-banner",
                                    }}
                                />
                            ) : svgContent ? (
                                <div className="space-y-3">
                                    {/* Zoom controls */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-1 rounded-md bg-surface-primary p-1">
                                            <button
                                                onClick={handleFitToScreen}
                                                className="hover:bg-surface-subtle rounded px-2 py-1 text-xs"
                                                title="Reset view"
                                                data-tracking-id="fittoscreen-mermaid-code-preview"
                                            >
                                                <Fullscreen size={16} />
                                            </button>
                                            <div className="h-4 w-px bg-border-subtle" />
                                            <button
                                                onClick={handleZoomOut}
                                                className="hover:bg-surface-subtle rounded px-2 py-1 text-xs"
                                                title="Zoom out"
                                                data-tracking-id="zoomout-mermaid-code-preview"
                                            >
                                                <ZoomOut size={16} />
                                            </button>
                                            <button
                                                onClick={handleZoomIn}
                                                className="hover:bg-surface-subtle rounded px-2 py-1 text-xs"
                                                title="Zoom in"
                                                data-tracking-id="zoomin-mermaid-code-preview"
                                            >
                                                <ZoomIn size={16} />
                                            </button>
                                            <div className="h-4 w-px bg-border-subtle" />
                                            <button
                                                onClick={handleFullscreen}
                                                className="hover:bg-surface-subtle rounded px-2 py-1 text-xs"
                                                title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                                                data-tracking-id="fullscreen-mermaid-code-preview"
                                            >
                                                {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
                                            </button>
                                        </div>
                                        <div className="text-xs text-content-muted">{Math.round(zoomLevel * 100)}%</div>
                                    </div>

                                    {/* Diagram container with zoom */}
                                    <div
                                        className="mermaid-scroll-container overflow-auto rounded-md border border-border-subtle bg-white"
                                        style={{
                                            scrollbarWidth: "thin",
                                            scrollbarColor: "rgba(0, 0, 0, 0.3) transparent",
                                        }}
                                    >
                                        <style
                                            dangerouslySetInnerHTML={{
                                                __html: `
                                                .mermaid-scroll-container::-webkit-scrollbar {
                                                    width: 6px;
                                                    height: 6px;
                                                }
                                                .mermaid-scroll-container::-webkit-scrollbar-track {
                                                    background: transparent;
                                                }
                                                .mermaid-scroll-container::-webkit-scrollbar-thumb {
                                                    background-color: rgba(0, 0, 0, 0.3);
                                                    border-radius: 6px;
                                                    border: 3px solid transparent;
                                                }
                                                .dark .mermaid-scroll-container::-webkit-scrollbar-thumb {
                                                    background-color: rgba(200, 200, 200, 0.3);
                                                }
                                                .mermaid-scroll-container::-webkit-scrollbar-corner {
                                                    background: transparent;
                                                }
                                                @media (prefers-color-scheme: dark) {
                                                    .mermaid-scroll-container {
                                                        scrollbar-color: rgba(200, 200, 200, 0.3) transparent;
                                                    }
                                                }
                                            `,
                                            }}
                                        />
                                        <div
                                            className="mermaid-container mermaid-scroll-container"
                                            style={{
                                                transform: `scale(${zoomLevel})`,
                                                transformOrigin: "top left",
                                                minHeight: "200px",
                                            }}
                                            dangerouslySetInnerHTML={{ __html: svgContent }}
                                        />
                                    </div>
                                </div>
                            ) : isRendering ? (
                                <div className="flex items-center justify-center py-8">
                                    <div className="flex items-center space-x-2 text-content-muted">
                                        <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                        <span>Rendering diagram...</span>
                                    </div>
                                </div>
                            ) : (
                                <div className="flex items-center justify-center py-8 text-content-muted">
                                    <span>Click Preview to render diagram</span>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </Tabs>
        </div>
    );
};
